import Channel, { ChannelStore } from "@/types/Channel.interface"
import Message, { MessageSendType } from "@/types/Message.interface"
import Person from "@/types/Person.interface"
import events from "@/events/events"
import { ChannelParams } from "@/types/PaginationParams.interface"
import Pagination from "@/types/Pagination.interface"
import helpers from "@/utility/helpers"
import { MessageParams } from "@/types/PaginationParams.interface"
import Group from "@/types/Group.interface"

interface State {
    inboxChannels: Channel[]                   //  The channels displayed in the Inbox
    inboxChannelsParams: ChannelParams         //  The inbox's current channel pagination params
    inboxChannelsParamsDefault: ChannelParams  //  The default chanels pagination params (immutable)
    inboxOpen: boolean                         //  The v-model for our open/closed toggle filter
    inboxPaginating: boolean                   //  Indicates inboxChannels pagination is in progress
    inboxSelectedChannel: Channel              //  The inbox's currently selected channel
    inboxSelectedChannelPaginating: boolean    //  The paginatiuon tracker for our selected channel
    inboxSelectedFilteredMessages: Message[]           //  The messages displayed in the inbox
    inboxSelectedFilteredMessagesLastPage: number      //  The last page of messages pagination (for current channel)
    inboxSelectedFilteredMessagesPaginating: boolean   //  Determines if we want are currently paginating messages
    inboxSelectedFilteredMessagesParams: MessageParams //  The pagination params for our messages
    inboxSelectedMessages: Message[]           //  The messages displayed in the inbox
    inboxSelectedMessagesLastPage: number      //  The last page of messages pagination (for current channel)
    inboxSelectedMessagesPaginating: boolean   //  Determines if we want are currently paginating messages
    inboxSelectedMessagesParams: MessageParams //  The pagination params for our messages
    inboxSelectedPerson: Person                //  The person displayed in the inbox
    inboxSelectedPersonPaginating: boolean     //  The person being paginated
    inboxSelectedScheduledMessages: Message[]  //  The schedules message that belong to the selected channel
    inboxSelectedBucket: Group                 //  The bucket group currently selected (defaults to null)
    inboxMessageType: string                   //  The type of messages we display
    inboxChannelsLastPagination: Date
}

const defaults = () => {
    return {
        inboxChannels: [],
        inboxChannelsCurrentPage: null,
        inboxChannelsLastPage: null,
        inboxChannelsNextPage: null,
        inboxChannelsParams: {
            assigned: 'everyone',
            bucket: null,
            hasMoreChannels: false,
            id: null,
            lastMessageTime: null,
            limit: 20,
            order_by: 'desc',
            status: null,
            type: 'open',
        },
        inboxChannelsParamsDefault: {
            assigned: 'everyone',
            bucket: null,
            hasMoreChannels: false,
            id: null,
            lastMessageTime: null,
            limit: 20,
            order_by: 'desc',
            status: null,
            type: 'open',
        },
        inboxOpen: true,
        inboxPaginating: false,
        inboxSelectedChannel: null,
        inboxSelectedChannelPaginating: false,
        inboxSelectedFilteredMessages: [],
        inboxSelectedFilteredMessagesLastPage: null,
        inboxSelectedFilteredMessagesPaginating: false,
        inboxSelectedFilteredMessagesParams: {
            page: 1,
            limit: 20,
            filters: ['channel']
        },
        inboxSelectedMessages: [],
        inboxSelectedMessagesLastPage: null,
        inboxSelectedMessagesPaginating: false,
        inboxSelectedMessagesParams: {
            page: 1,
            limit: 20,
            filters: ['channel']
        },
        inboxSelectedPerson: null,
        inboxSelectedPersonPaginating: false,
        inboxSelectedScheduledMessages: [],
        inboxSelectedBucket: null,
        inboxMessageType: 'channel',
        inboxChannelsLastPagination: null
    }
}

export default {
    state: defaults(),
    mutations: {
        inboxChannelsSort(state: State): void {
            //  Get our current params
            const params = state.inboxChannelsParams

            //  Determine our sorting order
            const desc = params.order_by === 'desc'

            //  Sort the state (this sorts by unread message count THEN last message time fucking sick i know )
            state.inboxChannels.sort((a: Channel, b: Channel) => {
                return desc
                    //  @ts-ignore
                    ? +window.getDate(b.last_message?.updated_at) - +window.getDate(a.last_message?.updated_at)
                    //@ts-ignore
                    : +window.getDate(a.last_message?.updated_at) - +window.getDate(b.last_message?.updated_at)
            })
        },
        inboxMessagePostChannelSync(state: State, message: Message): void {
            //  Grab the channel id of our message
            const channelId = message.channel_id

            //  Grab our channels
            const channels = state.inboxChannels

            //  Check if this id exists in our channels list
            const existing = channels.find((x: Channel) => x.id === channelId)

            //  If there aren't any matches then we don't do anything
            if (!existing) return

            //  @ts-ignore Else, update the last message and updated at
            existing.last_message_time = message.updated_at
            //  This might cause issues where the last message doesn't exist but i highly doubt it
            existing.last_message.text = message.text
            existing.last_message.updated_at = message.updated_at
            existing.updated_at = message.updated_at

            //  Update this channel
            state.inboxChannels = window.removeDuplicatesById(state.inboxChannels, [existing])
        },
        inboxMessagePostMessageSync(state: State, message: Message): void {
            //  Grab our selected channel
            const selected = state.inboxSelectedChannel

            //  If we don't have a selected channel then we are done
            if (!selected) return

            //  Check if the selected channel matches our message
            const match = selected.id === message.channel_id

            //  If it doesn't match then we are done
            if (!match) return

            //  Add our message to the list
            helpers.addItemById(message, state.inboxSelectedMessages, true)

            //  Grab our current type
            const type = state.inboxMessageType

            //  If the type is 'channel' we are done
            if (type === 'channel') return

            //  Filter our message based on what type
            const filtered: boolean = {
                comment: (msg: Message) => msg.type === 'comment',
                send: (msg: Message) => msg.type !== 'comment' && msg.status !== 'scheduled',
                scheduled: (msg: Message) => msg.status === 'scheduled'
            }[type](message)

            //  If this doesn't belong in the filter, then make sure we remove it
            if (!filtered) helpers.removeItemById(message, state.inboxSelectedFilteredMessages)

            //  Else, update our filtered message
            else helpers.addItemById(message, state.inboxSelectedFilteredMessages, true)
        },
        inboxChannelsParamsSet(state: State, params: ChannelParams): void {
            state.inboxChannelsParams = params
        },
        inboxChannelsPropertyPush(state: State, channel: Channel): void {
            //  Grab our global channels
            const channels = state.inboxChannels

            //  Find our channel from this
            const existing = channels.find((x: Channel) => x.id === channel.id)

            //  If it doesn't exist, do nothing
            if (!existing) return

            //  Else, update the channel (this is for reactivity)
            const updated = Object.assign({}, existing, channel)

            //  Replace with the updated
            state.inboxChannels = window.removeDuplicatesById(state.inboxChannels, [updated])

            //  Get the currently selected channel
            const selectedChannel = state.inboxSelectedChannel

            //  If the channel does not exist, we have nothing to do
            if (!selectedChannel) return

            //  Check if the selected channel id matches the updated
            const match = selectedChannel.id == updated.id

            //  If they don't match, we are done
            if (!match) return

            //  Else, update the selected channel
            state.inboxSelectedChannel = updated
        },
        inboxChannelsPush(state: State, channels: Channel[]): void {
            //  Update the state 
            state.inboxChannels = window.removeDuplicatesById(state.inboxChannels, channels)

            //  Get the currently selected channel
            const selectedChannel = state.inboxSelectedChannel

            //  If the channel does not exist, we have nothing to do
            if (!selectedChannel) return

            //  Else, find the selected channel in the channels list
            const updatedChannel = channels.find((x: Channel) => x.id == selectedChannel.id)

            //  If the selected channel was NOT updated, we have nothing to do, just return
            if (!updatedChannel) return

            //  Else, update the selected channel
            state.inboxSelectedChannel = updatedChannel

            //  Publish the channelsPushedEvent
            events.onChannelsPushed()
        },
        inboxChannelsRemove(state: State, ids: number[]): void {
            //  Get our current list of channels
            const channels = state.inboxChannels

            //  If no channels, do nothing
            if (!channels) return

            //  Else, filter our our removed ids
            const filtered = channels.filter(x => !ids.includes(x.id))

            //  Set our channels to the filtered list
            state.inboxChannels = filtered

            //  Get the currently selected channel
            const selectedChannel = state.inboxSelectedChannel

            //  If the channel does NOT exist, we have nothing to do
            if (!selectedChannel) return

            //  Else, find if our channel was deleted
            const deleted = ids.find(x => x == selectedChannel.id)

            //  If the selected channel was NOT deleted, we have nothing to do
            if (!deleted) return

            //  Else, update the selected channel to null since deleted
            state.inboxSelectedChannel = null
        },
        inboxChannelsSync(state: State, updates: Channel[]): void {
            //  Get our current params
            const params = state.inboxChannelsParams

            //  Get our last pagination time
            const lastPagination = state.inboxChannelsLastPagination

            //  Determine our sorting order
            const desc = params.order_by === 'desc'

            //  Get our current bucket
            const bucket = state.inboxSelectedBucket

            //  Apply our inline filtering
            const preFiltered = updates.appSyncFilter(params, 'inbox')

            //  Do an additional filter to remove ids not within the bucket
            //  We do this outside of appSyncFilter() for performance gains
            const filtered = bucket
                ? preFiltered.filter((x: Channel) => bucket.persons_ids.includes(x.person.id))
                : preFiltered

            //  Lets also filter our timestamps that are too late
            const timeFiltered = filtered.filter((x: Channel) => {
                //  Grab either open or closed from our channel and split it into parts
                const t = x[params.type].split(/[- :]/)

                //  Combine the parts so we can recreate the datetime as UTC
                const parsed = new Date(Date.UTC(t[0], t[1] - 1, t[2], t[3], t[4], t[5]))

                //  We only want channels that have been opened/closed BEFORE our last pagination
                //  Any channels opened/closed AFTER pagination are free to be added
                //  Channels opened/closed BEFORE pagination should already be there from the pagination
                return parsed >= lastPagination
            })

            //  Filtered contains our list of items to update/push so we need a difference of items to remove
            const remove = window._.differenceBy(updates, filtered, 'id')

            //  Sync the state
            state.inboxChannels = window.removeDuplicatesById(state.inboxChannels, timeFiltered, remove)

            //  Sort the state (this sorts by unread message count THEN last message time fucking sick i know )
            state.inboxChannels.sort((a: Channel, b: Channel) => {
                return desc
                    //  @ts-ignore
                    ? +window.getDate(b.last_message?.updated_at) - +window.getDate(a.last_message?.updated_at)
                    //@ts-ignore
                    : +window.getDate(a.last_message?.updated_at) - +window.getDate(b.last_message?.updated_at)
            })

            //  Get the currently selected channel
            const selectedChannel = state.inboxSelectedChannel

            //  If the channel does not exist, we have nothing to do
            if (!selectedChannel) return

            //  Else, find the selected channel in the channels list
            const exists = updates.find((x: Channel) => x.id == selectedChannel.id)

            //  If the selected channel was NOT updated, we have nothing to do, just return
            if (!exists) return

            //  Else, update the selected channel
            state.inboxSelectedChannel = exists
        },
        inboxChannelsPersonsUpdate(state: State, persons: Person[]) {
            for (const person of persons) {
                //  search if the person exists
                const i = state.inboxChannels.findIndex(x => x.person.id === person.id)

                //  If the person exists, update it
                if (i > -1) state.inboxChannels[i].person = person
            }
        },
        //  APP SYNC
        inboxSelectedFilteredMessagesSync(state: State, messages: Message[]): void {
            //  Get the currently selected channel id
            const channel = state.inboxSelectedChannel

            //  If a selected channel does NOT exist, we have nothing to do
            if (!channel) return

            //  Grab our current type
            const type = state.inboxMessageType

            //  If the type is 'channel' we are done
            if (type === 'channel') return

            //  Our filter function for filtering messages
            const msgFilter = {
                comment: (msg: Message) => msg.type === 'comment',
                send: (msg: Message) => msg.type !== 'comment' && msg.status !== 'scheduled',
                scheduled: (msg: Message) => msg.status === 'scheduled'
            }

            //  Filter our messages based on the type
            const filtered = messages.filter((x: Message) => msgFilter[type](x))

            //  Get messages that belong to the selected channel
            const msgs = filtered.filter(x => x.channel_id == channel.id)

            //  Add each message to the messages list
            for (const x of msgs) helpers.addItemById(x, state.inboxSelectedFilteredMessages, true)
        },
        inboxSelectedMessagesSync(state: State, messages: Message[]): void {
            //  Get the currently selected channel id
            const channel = state.inboxSelectedChannel

            //  If a selected channel does NOT exist, we have nothing to do
            if (!channel) return

            //  Get messages that belong to the selected channel AND are not scheduled
            const msgs = messages.filter(x => x.channel_id == channel.id && !x.schedule_id)

            //  Add each message to the messages list
            for (const x of msgs) helpers.addItemById(x, state.inboxSelectedMessages, true)
        },
        inboxOpenSet(state: State, bool: boolean): void {
            state.inboxOpen = bool
        },
        inboxPaginatingSet(state: State, bool: boolean): void {
            state.inboxPaginating = bool
        },
        inboxPaginationPush(state: State, params: ChannelParams) {
            //  If we have reset our cursor, reset the the channels list state as a filter was applied (open/closed or some other filter)
            if (!params.id && !params.lastMessageTime) {
                state.inboxChannels = []
            }

            //  We ensure that we update the hasMoreChannels property here because the api passes this up to the client, without we cannot render the 'show more' button
            state.inboxChannelsParams.hasMoreChannels = params.hasMoreChannels

            //  Set our last pagination time to right now
            state.inboxChannelsLastPagination = new Date()
        },
        inboxSelectedChannelPaginatingSet(state: State, bool: boolean): void {
            state.inboxSelectedChannelPaginating = bool
        },
        inboxSelectedChannelSet(state: State, channel: Channel): void {
            state.inboxSelectedChannel = channel
        },
        //  SELECTED CHANNEL MESSAGES
        inboxSelectedFilteredMessagesParamsReset(state: State): void {
            state.inboxSelectedFilteredMessagesParams.page = 1
            //state.inboxSelectedFilteredMessagesLastPage = null
        },
        inboxSelectedFilteredMessagesParamsSet(state: State, params: MessageParams): void {
            state.inboxSelectedFilteredMessagesParams = params
        },
        inboxSelectedFilteredMessagesSet(state: State, messages: Message[]): void {
            state.inboxSelectedFilteredMessages = messages
        },
        inboxSelectedFilteredMessagesPropertyPush(state: State, message: Message): void {
            //  Grab our global messages
            const messages = state.inboxSelectedFilteredMessages

            //  If no selected messages, do nothing
            if (!messages) return

            //  Find our messages from this
            const existing = messages.find((x: Message) => x.id === message.id)

            //  If it doesn't exist, do nothing
            if (!existing) return

            //  Else, update the messages (this is for reactivity)
            const updated = Object.assign({}, existing, message)

            //  Replace with the updated
            state.inboxSelectedFilteredMessages = window.removeDuplicatesById(state.inboxSelectedFilteredMessages, [updated])
        },
        inboxSelectedFilteredMessagesPush(state: State, messages: Message[]): void {
            state.inboxSelectedFilteredMessages = window.removeDuplicatesById(state.inboxSelectedFilteredMessages, messages)
        },
        inboxSelectedFilteredMessagesRemove(state: State, ids: number[]): void {
            //  Grab our current list of messages
            const messages = state.inboxSelectedFilteredMessages

            //  If no messages, do nothing
            if (!messages) return

            //  Else, filter out our ids
            const filtered = messages.filter((x: Message) => !ids.includes(x.id))

            //  Replace our list with the filtered
            state.inboxSelectedFilteredMessages = filtered
        },
        //  Sets the current channel id on our pagination params for messages
        inboxSelectedFilteredMessagesChannelSet(state: State, channelId: number): void {
            state.inboxSelectedFilteredMessagesParams.channel_id = channelId
        },
        inboxSelectedFilteredMessagesPaginatingSet(state: State, bool: boolean): void {
            state.inboxSelectedFilteredMessagesPaginating = bool
        },
        //  Sets the pagination params for our selected messages
        inboxSelectedFilteredMessagesPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the messages list
            if (page.current_page === 1) state.inboxSelectedFilteredMessages = []

            //  Set our pages
            state.inboxSelectedFilteredMessagesLastPage = page.last_page
        },
        inboxSelectedMessagesParamsReset(state: State): void {
            state.inboxSelectedMessagesParams.page = 1
            //state.inboxSelectedMessagesLastPage = null
        },
        inboxSelectedMessagesParamsSet(state: State, params: MessageParams): void {
            state.inboxSelectedMessagesParams = params
        },
        inboxSelectedMessagesSet(state: State, messages: Message[]): void {
            state.inboxSelectedMessages = messages
        },
        inboxSelectedMessagesPropertyPush(state: State, message: Message): void {
            //  Grab our global messages
            const messages = state.inboxSelectedMessages

            //  If no selected messages, do nothing
            if (!messages) return

            //  Find our messages from this
            const existing = messages.find((x: Message) => x.id === message.id)

            //  If it doesn't exist, do nothing
            if (!existing) return

            //  Else, update the messages (this is for reactivity)
            const updated = Object.assign({}, existing, message)

            //  Replace with the updated
            state.inboxSelectedMessages = window.removeDuplicatesById(state.inboxSelectedMessages, [updated])
        },
        inboxSelectedMessagesPush(state: State, messages: Message[]): void {
            state.inboxSelectedMessages = window.removeDuplicatesById(state.inboxSelectedMessages, messages)
        },
        inboxSelectedMessagesRemove(state: State, ids: number[]): void {
            //  Grab our current list of messages
            const messages = state.inboxSelectedMessages

            //  If no messages, do nothing
            if (!messages) return

            //  Else, filter out our ids
            const filtered = messages.filter((x: Message) => !ids.includes(x.id))

            //  Replace our list with the filtered
            state.inboxSelectedMessages = filtered
        },
        //  Sets the current channel id on our pagination params for messages
        inboxSelectedMessagesChannelSet(state: State, channelId: number): void {
            state.inboxSelectedMessagesParams.channel_id = channelId
        },
        inboxSelectedMessagesPaginatingSet(state: State, bool: boolean): void {
            state.inboxSelectedMessagesPaginating = bool
        },
        //  Sets the pagination params for our selected messages
        inboxSelectedMessagesPaginationPush(state: State, page: Pagination): void {
            //  If this is page one, reset the messages list
            if (page.current_page === 1) state.inboxSelectedMessages = []

            //  Set our pages
            state.inboxSelectedMessagesLastPage = page.last_page
        },
        //  SELECTED PERSON
        inboxSelectedPersonPaginatingSet(state: State, bool: boolean): void {
            state.inboxSelectedPersonPaginating = bool
        },
        inboxSelectedPersonRemove(state: State, ids: number[]): void {
            //  Get the currently selected person
            const selectedPerson = state.inboxSelectedPerson

            //  If the person does NOT exist, we have nothing to do
            if (!selectedPerson) return

            //  Else, find if our person was deleted
            const deleted = ids.find(x => x == selectedPerson.id)

            //  If the selected person was NOT deleted, we have nothing to do
            if (!deleted) return

            //  Else, update the selected person to null since deleted
            state.inboxSelectedPerson = null
        },
        inboxSelectedPersonSet(state: State, person: Person): void {
            state.inboxSelectedPerson = person
        },
        inboxSelectedPersonSync(state: State, updates: Person[]): void {
            //  Grab our currently selected person
            const selectedPerson = state.inboxSelectedPerson

            //  If we don't have a selected person, we are done
            if (!selectedPerson) return

            //  Find if our selected person is in the updates
            const updatedPerson = updates.find((x: Person) => x.id === selectedPerson.id)

            //  If we have not updated this person, do nothing
            if (!updatedPerson) return

            //  Else, replace our person
            state.inboxSelectedPerson = updatedPerson
        },
        //  SCHEDULED MESSAGES
        inboxSelectedScheduledMessagesRemove(state: State, ids: number[]): void {
            //  Grab our current list of scheduled messages
            const messages = state.inboxSelectedScheduledMessages

            //  If no messages, do nothing
            if (!messages) return

            //  Else, filter out our deleted ids
            const filtered = messages.filter((x: Message) => !ids.includes(x.id))

            //  Replace our list with the filtered
            state.inboxSelectedScheduledMessages = filtered
        },
        inboxSelectedScheduledMessagesSync(state: State, messages: Message[]): void {
            //  Get the currently selected channel
            const channel = state.inboxSelectedChannel

            //  If a selected channel does NOT exist, we have nothing to do
            if (!channel) return

            //  Get messages that belong to the selected channel
            const msgs = messages.filter(x => x.channel_id == channel.id)

            //  Add each message to the messages list
            for (const x of msgs) helpers.addItemById(x, state.inboxSelectedScheduledMessages, true)
        },
        inboxSelectedScheduledMessagesSet(state: State, messages: Message[]): void {
            state.inboxSelectedScheduledMessages = messages
        },
        inboxSelectedBucketRemove(state: State, ids: number[]): void {
            //  Grab our selected bucket
            const bucket = state.inboxSelectedBucket

            //  If no bucket, do nothing
            if (!bucket) return

            //  Else, check if this bucket was deleted
            const deleted = ids.includes(bucket.id)

            //  If it wasn't, do nothing
            if (!deleted) return

            //  Else, set our bucket to null
            state.inboxSelectedBucket = null
        },
        inboxSelectedBucketSet(state: State, bucket: Group): void {
            state.inboxSelectedBucket = bucket
        },
        inboxSelectedBucketSync(state: State, groups: Group[]): void {
            //  Grab our selected bucket
            const bucket = state.inboxSelectedBucket

            //  If there is no bucket, do nothing
            if (!bucket) return

            //  Else, find this bucket from our list of groups
            const updated = groups.find((x: Group) => x.id === bucket.id)

            //  If the updated bucket wasn't found, do nothing
            if (!updated) return

            //  Else, make sure we update our bucket with the data from this group
            state.inboxSelectedBucket = window._.extend(bucket, updated)
        },
        inboxMessageTypeSet(state: State, type: MessageSendType) {
            state.inboxMessageType = type
        },
        inboxReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        inboxChannelsPaginate({ commit, getters }): Promise<Pagination> {
            //  Select our pagination params (if no input, use default params)
            const p = getters.inboxChannelsParams

            //  Start the 'paginating' tracker
            commit('inboxPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/channels?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {

                //  Update pagination params (this must occur BEFORE updating the channels list, for reset to work properly)
                commit('inboxPaginationPush', response.data)

                //  Update the inbox channels list
                commit('inboxChannelsPush', response.data.data)

                //  Stop the 'paginating' tracker
                commit('inboxPaginatingSet', false)

                //  Return the pagination data
                return response.data
            }).catch(() => null)
        },
        inboxSelectedChannelGet({ commit, getters }, channelId: number): Promise<void> {
            //  Start the 'paginating' tracker
            commit('inboxSelectedChannelPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/channels/${channelId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set the inbox selected channel
                commit('inboxSelectedChannelSet', response.data)

                //  Stop the 'paginating' tracker
                commit('inboxSelectedChannelPaginatingSet', false)

                return response.data
            }).catch(() => null)
        },
        inboxSelectedFilteredMessagesPaginate({ commit, getters }, channelId: number): Promise<void> {
            //  Grab our pagination params
            const p = getters.inboxSelectedFilteredMessagesParams

            //  Attach our channel id
            p.channel_id = channelId

            //  Start our pagination tracker
            commit('inboxSelectedFilteredMessagesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/messages?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Update pagination params
                commit('inboxSelectedFilteredMessagesPaginationPush', response.data)

                //  Set the inbox selected messages
                commit('inboxSelectedFilteredMessagesPush', response.data.data)

                //  Stop the pagination tracker
                commit('inboxSelectedFilteredMessagesPaginatingSet', false)

            }).catch(e => console.error("inboxSelectedFilteredMessagesPaginate ERROR", e))
        },
        inboxSelectedMessagesPaginate({ commit, getters }, channelId: number) {
            //  Grab our pagination params
            const p = getters.inboxSelectedMessagesParams

            //  Attach our channel id
            p.channel_id = channelId

            //  If we're paginating the first page, we need to check the cache for the message
            if (p.page === 1) {
                //  Get the messages cache
                const cache = getters.messagesCache

                //  Search for the existing messages in the cache
                const channelStore: ChannelStore = cache.find(x => x.channelID == channelId)

                //  If we have cached messages, use them
                if (channelStore) {
                    //  Update inbox pagination params trackers
                    commit('inboxSelectedMessagesPaginationPush', { current_page: 1, last_page: channelStore.lastPage })

                    //  Load messages from the channel store
                    commit('inboxSelectedMessagesSet', channelStore.messages)

                    //  Update the channelStore 'loaded' timestamp
                    channelStore.loaded = Date.now()
                    commit('messagesSet', channelStore)

                    //  Do nothing else
                    return channelStore.messages
                }
            }

            //  Else, we need to paginate, Start our pagination tracker
            commit('inboxSelectedMessagesPaginatingSet', true)

            //  Assemble the API endpoint
            const url = `${getters.api}/messages?${window.toUrlParams(p)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Grab our current selected channel
                const channel = getters.inboxSelectedChannel

                //  If our current channel does match our channel id then we can continue
                //  This helps resolve a race condition of frequent switching of channels
                if (channel?.id === channelId) {
                    //  Update pagination params
                    commit('inboxSelectedMessagesPaginationPush', response.data)

                    //  Set the inbox selected messages
                    commit('inboxSelectedMessagesPush', response.data.data)

                    //  If this is page one, we need to save it to local storage
                    if (response.data.current_page === 1) {

                        //  Assemble the channel store
                        const store: ChannelStore = {
                            channelID: channelId,
                            lastPage: response.data.last_page,
                            messages: response.data.data,
                            loaded: Date.now()
                        }

                        //  Save the channel store
                        commit('messagesSet', store)
                    }
                }

                //  Stop the pagination tracker
                commit('inboxSelectedMessagesPaginatingSet', false)

                //  Return our messages
                return response.data.data
            }).catch(e => console.error("InboxSelectedMessagesPaginate ERROR", e))
        },
        inboxSelectedPersonGet({ commit, getters }, channel: Channel) {
            //  Grab the person id from our channel
            const personId = channel.person.id

            //  Get the global persons list
            const persons = getters.persons

            //  Search for the exsiting person in the global dataset
            const existingPerson = persons.find(x => x.id == personId)

            //  If we found an existing person, return it
            if (existingPerson) return commit('inboxSelectedPersonSet', existingPerson)

            //  Start our paginating tracker for our person
            commit('inboxSelectedPersonPaginatingSet', true)

            //  Else, assemble the API endpoint
            const url = `${getters.api}/persons/${personId}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Grab our current selected channel
                const selected = getters.inboxSelectedChannel

                //  If our current channel does match our channel id then we can continue
                //  This helps resolve a race condition of frequent switching of channels
                if (selected?.id === channel.id) {
                    //  Set the inbox selected channel
                    commit('inboxSelectedPersonSet', response.data)

                    //  Add the channel to the global dataset
                    commit('personsPush', [response.data])
                }

                //  Stop our paginating tracker for our person
                commit('inboxSelectedPersonPaginatingSet', false)

                return response.data
            }).catch(() => null)
        },
        inboxSelectedScheduledMessagesGet({ commit, getters }, channelId: number): Promise<void> {
            //  Assemble pagination params
            const params = {
                page: 1,
                limit: 100,
                channel_id: channelId,
                filters: ['scheduled']
            }

            //  Assemble the API endpoint
            const url = `${getters.api}/messages?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                commit('inboxSelectedScheduledMessagesSet', response.data.data)
            })
        }
    },
    getters: {
        inboxChannels(state: State): Array<Channel> {
            return state.inboxChannels
        },
        inboxChannelsParams(state: State): ChannelParams {
            return state.inboxChannelsParams
        },
        inboxChannelsParamsDefault(state: State): ChannelParams {
            return state.inboxChannelsParamsDefault
        },
        inboxOpen(state: State): boolean {
            return state.inboxOpen
        },
        inboxPaginating(state: State): boolean {
            return state.inboxPaginating
        },
        inboxSelectedChannel(state: State): Channel {
            return state.inboxSelectedChannel
        },
        inboxSelectedChannelPaginating(state: State): boolean {
            return state.inboxSelectedChannelPaginating
        },
        inboxSelectedFilteredMessages(state: State): Message[] {
            return state.inboxSelectedFilteredMessages
        },
        inboxSelectedFilteredMessagesLastPage(state: State): number {
            return state.inboxSelectedFilteredMessagesLastPage
        },
        inboxSelectedFilteredMessagesPaginating(state: State): boolean {
            return state.inboxSelectedFilteredMessagesPaginating
        },
        inboxSelectedFilteredMessagesParams(state: State): MessageParams {
            return state.inboxSelectedFilteredMessagesParams
        },
        inboxSelectedMessages(state: State): Message[] {
            return state.inboxSelectedMessages
        },
        inboxSelectedMessagesLastPage(state: State): number {
            return state.inboxSelectedMessagesLastPage
        },
        inboxSelectedMessagesPaginating(state: State): boolean {
            return state.inboxSelectedMessagesPaginating
        },
        inboxSelectedMessagesParams(state: State): MessageParams {
            return state.inboxSelectedMessagesParams
        },
        inboxSelectedScheduledMessages(state: State): Message[] {
            return state.inboxSelectedScheduledMessages
        },
        inboxSelectedPerson(state: State): Person {
            return state.inboxSelectedPerson
        },
        inboxSelectedPersonPaginating(state: State): boolean {
            return state.inboxSelectedPersonPaginating
        },
        inboxSelectedBucket(state: State): Group {
            return state.inboxSelectedBucket
        },
        inboxMessageType(state: State): string {
            return state.inboxMessageType
        }
    }
}